//#region Imports & External Dependencies
import { computed, makeObservable, observable, runInAction } from "mobx";
import { ViewModelBase } from "@shoothill/core";
import { APIClient, RelayCommand } from "Application";
import { PastMentorsModel } from "./PastMentorsModel";
import { AppUrls } from "AppUrls";
import { MentorModel } from "Views/Mentors/Mentors/MentorModel";
import { ISearchBoxProps } from "Components/SearchBox/SearchBox";
import { GetFilteredMentorsRequest, GetFilteredMentorsResponse, POST_GetFilteredMentorsEndpoint } from "Views/Mentors/Mentors/EndPoints/POST_GetFilteredMentorsEndpoint";
import { MentorStatusEnum } from "Views/Mentors/Mentors/Enums/MentorStatusEnum";
//#endregion

//#region Class Definition
export class PastMentorsViewModel extends ViewModelBase<PastMentorsModel> {
    //#region Properties & State
    public apiClient = new APIClient();
    public _isLoadingData: boolean = false;
    public _render: boolean = false;
    public loadTimeoutId: NodeJS.Timeout | null = null;
    //#endregion

    //#region Constructor/Initialization
    constructor() {
        super(new PastMentorsModel());
        makeObservable(this, {
            _isLoadingData: observable,
            _render: observable,
            loadTimeoutId: observable,
            mentors: computed,
            isLoadingData: computed,
            render: computed,
        });
    }
    //#endregion

    //#region Computed Properties & Getters
    public get mentors() {
        return this.model.mentors.slice();
    }

    public get isLoadingData() {
        return this._isLoadingData;
    }

    public get render() {
        return this._render;
    }

    public get searchBoxProps(): ISearchBoxProps {
        return {
            value: this.model.filterKeyword,
            command: this.updateFilterKeywordCommand,
        };
    }
    //#endregion

    //#region Public Methods & Commands
    public setIsLoadingData = (isLoading: boolean) => {
        this._isLoadingData = isLoading;
    };

    public setRender = (render: boolean) => {
        this._render = render;
    };

    public reset = () => {
        this.setRender(false);
        this.setIsLoadingData(false);
        this.model.filterKeyword = "";
    };

    public setLoadTimeoutId = (loadTimeoutId: NodeJS.Timeout) => {
        this.loadTimeoutId = loadTimeoutId;
    };

    public navigateToMentorDetailsCommand = new RelayCommand((id: string) => {
        this.history.push(AppUrls.Client.Mentors.MentorDetail.Edit.replace(":id", id));
    });

    public updateFilterKeywordCommand = new RelayCommand((keyword: string) => {
        this.onUpdateKeywordSearch(keyword);
    });

    public navigateToAddMentorCommand = new RelayCommand(
        () => {
            this.history.push(AppUrls.Client.Mentors.MentorDetail.Root);
        },
        () => this.apiClient.IsBusy === false,
    );

    public resetApiClientErrorCommand = new RelayCommand(() => {
        this.apiClient.reset();
    });
    //#endregion

    //#region Event Handlers
    public onUpdateKeywordSearch = (v: string) => {
        const previousSearchString = this.model.filterKeyword;
        this.setValue("filterKeyword", v);
        const isEmptySearch = this.model.filterKeyword.length === 0;
        const isValidSearch = this.model.filterKeyword.length > 1 && this.model.filterKeyword !== previousSearchString;
        this.setValue("filterKeyword", v);
        if (isEmptySearch || isValidSearch) {
            this.debouncedLoadMentorsAsync();
        }
    };
    //#endregion

    //#region API Methods
    public getFilteredMentors = async () => {
        this.setIsLoadingData(true);
        const request: GetFilteredMentorsRequest = new GetFilteredMentorsRequest();
        request.keyword = this.model.filterKeyword;
        request.statusType = MentorStatusEnum.Past;
        // You might need to adjust this endpoint or create a new one for past mentors
        await this.apiClient.sendAsync(new POST_GetFilteredMentorsEndpoint(), request);
        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                const response: GetFilteredMentorsResponse = this.apiClient.Response();
                const models = response.items.map((i) => {
                    const m = new MentorModel();
                    m.fromResponse(i);
                    return m;
                });
                this.model.mentors.replace(models);
            });
            this.setIsLoadingData(false);
            this.setRender(true);
        } else {
            this.setIsLoadingData(false);
        }
    };
    //#endregion

    //#region Helper Methods
    public debouncedLoading = (callback: () => void, delayMs: number = 500) => {
        if (this.loadTimeoutId) {
            clearTimeout(this.loadTimeoutId);
        }

        this.setLoadTimeoutId(
            setTimeout(() => {
                callback();
            }, delayMs),
        );
    };

    public debouncedLoadMentorsAsync = () => {
        this.debouncedLoading(this.getFilteredMentors, 500);
    };
    //#endregion
}
//#endregion
